import Parse from 'parse';
import { Injectable, Inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { BreadcrumbNavigationEffects } from '../../effects';
import { NavigationStore } from '../../stores';
import { SampleAiAnalysisProgressStore, algorithmAiAnalysisLocked } from '../microscope-image/state';
import { Category, ProtocolScope, ProtocolStore } from '@telespot/protocols/ui';
import {
  FindingStore,
  MicroLayoutStore,
  VideoRoiStore,
  AssetStore,
  AnalysisStateStore,
  AnalystStore,
  ANALYSIS_STATE_SERVICE,
  RoiStatsStore,
} from '../../stores';
import { RoiStatsEffects } from '../../effects';
import { EMPTY, Observable, Subject, from } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { combineLatest, merge, of } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { getRouterSelectors } from '@ngrx/router-store';
import { ViewerMode, DrawingMode, RulerType, ETabContext, ImageFilter, CounterMode } from '../../types/viewer.types';
import { StatsMode } from '../stats-settings/stats-settings.component';
import { Analyst, IState } from '@analysis/domain';
// Concrete class (not the AnalysisStateRepository port): the @Inject()
// parameter type must be a runtime value, because jest's downleveled
// ctorParameters references the annotation's identifier.
import { TelespotAnalysisStateRepository } from '@analysis/infra/telespot';
import type { AnalyzerEntity, FindingParameters } from '@telespot/domain';
import { VideoROIOverlay } from '@shared/viewers/feature';
import { Router } from '@angular/router';
import { StepTask } from '@telespot/domain';
import { FindingItem, IVideoROI } from '@analysis/domain';
import { FeaturesService } from '@telespot/web-core';

@Injectable()
export class MicroscopeVideoFacade extends ComponentStore<Record<string, never>> {
  private readonly saveBlockedByAiAnalysisSubject = new Subject<void>();
  public readonly saveBlockedByAiAnalysis$ = this.saveBlockedByAiAnalysisSubject.asObservable();

  private readonly navigateToCaseDetailTrigger$ = new Subject<void>();
  private readonly loadAnalysts$ = new Subject<{ sampleId: string; caseId: string }>();
  private readonly syncChangesTrigger$ = new Subject<void>();
  private readonly discardChangesTrigger$ = new Subject<void>();

  constructor(
    private readonly navigationStore: NavigationStore,
    private readonly breadcrumbNavigationEffects: BreadcrumbNavigationEffects,
    private readonly protocolStore: ProtocolStore,
    private readonly microLayout: MicroLayoutStore,
    private readonly globalStore: Store,
    private readonly findingStore: FindingStore,
    private readonly videoRoiStore: VideoRoiStore,
    private readonly assetStore: AssetStore,
    private readonly router: Router,
    private readonly analysisStateStore: AnalysisStateStore,
    private readonly analystStore: AnalystStore,
    private readonly roiStatsStore: RoiStatsStore,
    private readonly roiStatsEffects: RoiStatsEffects,
    @Inject(ANALYSIS_STATE_SERVICE) private readonly analysisStateApi: TelespotAnalysisStateRepository,
    private readonly sampleAiProgressStore: SampleAiAnalysisProgressStore,
    private readonly featuresService: FeaturesService
  ) {
    super({});

    // Breadcrumb dropdown options are opt-in (see BreadcrumbNavigationEffects);
    // load them so the shared top-bar breadcrumb can cascade workspace → case → sample.
    this.breadcrumbNavigationEffects.loadBreadcrumbOptions();

    this.navigateToCaseDetailInput$.subscribe();
    this.loadAnalystsEffect(this.loadAnalysts$);
    this.syncChangesEffect(this.syncChangesTrigger$);
    this.discardChangesEffect(this.discardChangesTrigger$);

    const onSampleIdChanged$ = this.sampleId$.pipe(
      filter((sampleId) => !!sampleId),
      distinctUntilChanged()
    );

    const onCaseIdChanged$ = this.caseId$.pipe(
      filter((caseId) => !!caseId),
      distinctUntilChanged()
    );

    onSampleIdChanged$.subscribe((sampleId) => {
      this.loadSampleAssets(sampleId);
      this.fetchNavigationReferences(sampleId);
      this.loadProgress(of(sampleId));
      this.fetchProtocol(of(sampleId));
    });

    // Set current analyst when sampleId or caseId changes
    combineLatest([onSampleIdChanged$, onCaseIdChanged$])
      .pipe(
        filter(([sampleId, caseId]) => !!sampleId && !!caseId),
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1])
      )
      .subscribe(([sampleId, caseId]) => {
        this.setCurrentAnalyst(of({ sampleId, caseId }));
      });

    this.assetParam$
      .pipe(
        withLatestFrom(this.selectedAsset$),
        filter(([paramAsset, currentAsset]) => paramAsset !== currentAsset?.id),
        distinctUntilChanged()
      )
      .subscribe(([paramAsset]) => {
        this.selectAsset(of(paramAsset));
      });

    combineLatest([this.assetStore.items$, this.selectedAssetId$])
      .pipe(filter(([items, selectedId]) => items.length > 0 && !selectedId))
      .subscribe(() => this.selectAsset(of(null)));

    // AI progress polling/status is only wired when the organization is
    // licensed for AI review; otherwise no AI status surfaces in the viewer.
    const canUseAI = this.featuresService.canUse('ai');
    if (canUseAI) {
      this.wireSampleAiProgressLoad();
    }
    this.wireAiAnalysisViewOnly();
    if (canUseAI) {
      this.wireAiProgressDataRefresh();
    }

    const protocolLoadingComplete$ = this.protocolStore.loading$.pipe(
      pairwise(),
      filter(([prev, curr]) => !!prev && !curr)
    );
    const onLoadWithHistoryId$ = protocolLoadingComplete$.pipe(
      concatLatestFrom(() => this.historyId$),
      filter(([, historyId]) => !!historyId),
      map(([, historyId]) => historyId)
    );
    const onLoadWithoutHistoryId$ = protocolLoadingComplete$.pipe(
      concatLatestFrom(() => [this.historyId$, this.sampleId$]),
      filter(([, historyId]) => !historyId),
      map(([, , sampleId]) => sampleId),
      distinctUntilChanged()
    );
    const onHistoryChanged$ = this.historyId$.pipe(
      concatLatestFrom(() => this.protocolStore.loading$),
      filter(([historyId, protocolLoading]) => !protocolLoading && !!historyId),
      map(([historyId]) => historyId)
    );
    const onHistoryIdRemoved$ = this.historyId$.pipe(
      pairwise(),
      filter(([prev, curr]) => !!prev && !curr),
      concatLatestFrom(() => [this.protocolStore.loading$, this.sampleId$]),
      filter(([, protocolLoading, sampleId]) => !protocolLoading && !!sampleId),
      map(([, , sampleId]) => sampleId),
      distinctUntilChanged()
    );
    this.analystStore.getHistory(merge(onHistoryChanged$, onLoadWithHistoryId$));
    this.analystStore.getCurrentAnalyst(merge(onHistoryIdRemoved$, onLoadWithoutHistoryId$));

    this.historyId$
      .pipe(distinctUntilChanged())
      .subscribe((historyId) => {
        combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
          .pipe(take(1))
          .subscribe(([status, selectedAnalyst]) => {
            if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
              this.microLayout.setViewerMode(ViewerMode.idle);
              return;
            }
            this.microLayout.setViewerMode(historyId ? ViewerMode.idle : ViewerMode.drawing);
          });
      });

    combineLatest([this.ready$, this.selectedAssetId$, this.historyId$, this.sampleId$])
      .pipe(
        distinctUntilChanged(),
        filter(([ready, assetId, , sampleId]) => ready && !!assetId && !!sampleId)
      )
      .subscribe(([, assetId, historyId, sampleId]) => {
        this.fetchAssetFindings({
          sampleId,
          assetId,
          historyId,
        });
        this.deselectRois();
      });

    combineLatest([this.selectedAnalyst$, this.sampleId$])
      .pipe(
        distinctUntilChanged(),
        filter(([analyst, sampleId]) => !!analyst && !!sampleId)
      )
      .subscribe(([analyst, sampleId]) => {
        this.fetchSampleFindings({ sampleId, analyst });
      });

    combineLatest([this.loading$, this.items$, this.selectedAnalyst$, this.sampleId$])
      .pipe(filter(([loading, assets, analyst, sampleId]) => !loading && assets.length > 0 && !!analyst && !!sampleId))
      .subscribe(([, , analyst, sampleId]) => {
        this.fetchReferenceStrip({ sampleId, analyst });
      });
  }

  // #region Analyst and analysis states selectors
  public readonly userAnalysts$ = this.analystStore.userAnalysts$;
  public readonly aiAlgorithms$ = this.analystStore.aiAlgorithms$;
  public readonly isAnalystCurrentUser$ = this.analystStore.selectedAnalystIsCurrentUser$;
  public readonly selectedAnalyst$ = this.analystStore.selectedAnalyst$;
  public readonly reviewedAnalyst$ = this.analystStore.reviewedAnalyst$;
  public readonly isReviewingOtherAnalyst$ = this.analystStore.isReviewingOtherAnalyst$;
  public readonly analysisStates$ = this.analysisStateStore.analysisStates$;
  public readonly favouriteAssets$ = this.analysisStateStore.favouriteAssets$;

  public readonly findingsLoading$ = this.findingStore.loading$;

  public readonly analysisStatesLoading$ = this.select(
    this.analysisStates$,
    (analysisStates) => !analysisStates || analysisStates.size === 0
  );

  public readonly analystsLoading$ = this.select(
    this.analystStore.analysts$,
    this.analystStore.loading$,
    (analysts, loading) => loading || !analysts || analysts.length === 0
  );

  public readonly stateInfoFromSelectedAnalyst$ = this.select(
    this.selectedAnalyst$,
    this.analysisStates$,
    (selectedAnalyst, analysisStates) => {
      if (!selectedAnalyst) return null;
      return analysisStates.get(selectedAnalyst.id);
    }
  );

  public readonly currentAnalystFavouriteAssets$ = this.select(
    this.favouriteAssets$,
    this.stateInfoFromSelectedAnalyst$,
    (favouriteAssets, selectedAnalystState) => {
      return selectedAnalystState ? favouriteAssets.get(selectedAnalystState.id) || [] : [];
    }
  );

  /** Bookmarked asset of the selected analyst, sourced from the persisted analysis
   *  state so it survives reloads (mirrors the image screen). */
  public readonly currentAnalystBookmarkedAsset$ = this.select(
    this.analysisStateStore.bookmarkedAssets$,
    this.stateInfoFromSelectedAnalyst$,
    (bookmarkedAssets, selectedAnalystState) =>
      selectedAnalystState ? bookmarkedAssets.get(selectedAnalystState.id) ?? null : null
  );

  public readonly editingEnabled$ = this.microLayout.editingEnabled$;

  public readonly aiAnalysisInProgress$ = this.sampleAiProgressStore.inProgress$;

  public readonly aiAnalysisStatus$ = this.sampleAiProgressStore.status$;

  public readonly aiAnalysisViewOnly$ = this.select(
    this.aiAnalysisStatus$,
    this.selectedAnalyst$,
    (status, selectedAnalyst) => algorithmAiAnalysisLocked(status, selectedAnalyst)
  );

  public readonly canEdit$ = this.select(
    this.selectedAnalyst$,
    this.editingEnabled$,
    this.aiAnalysisStatus$,
    (selectedAnalyst, editingEnabled, status) => {
      if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
        return false;
      }
      const isReviewingOtherAnalyst = selectedAnalyst ? selectedAnalyst.id !== Parse.User.current()?.id : false;
      return !isReviewingOtherAnalyst || editingEnabled;
    }
  );

  private wireAiAnalysisViewOnly(): void {
    combineLatest([this.aiAnalysisStatus$, this.selectedAnalyst$])
      .pipe(filter(([status, selectedAnalyst]) => algorithmAiAnalysisLocked(status, selectedAnalyst)))
      .subscribe(() => {
        this.microLayout.toggleEditing(false);
        this.microLayout.setViewerMode(ViewerMode.idle);
      });
  }

  /**
   * While an algorithm analyst's execution is polling, the AI keeps marking
   * assets as analyzed server-side. `status$` stays `inProgress` and won't
   * re-emit, so we use the store's throttled `dataRefreshTick$` to refresh the
   * analyzed-asset markers in the reference strip without a reload.
   */
  private wireAiProgressDataRefresh(): void {
    this.fetchReferenceStrip(
      this.sampleAiProgressStore.dataRefreshTick$.pipe(
        concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$]),
        filter(([, sampleId, analyst]) => !!sampleId && !!analyst),
        map(([, sampleId, analyst]) => ({ sampleId: sampleId as string, analyst: analyst as Analyst }))
      )
    );
  }

  private wireSampleAiProgressLoad(): void {
    combineLatest([this.sampleId$, this.methodTypeId$, this.selectedAnalyst$])
      .pipe(
        map(([sampleId, methodTypeId, selectedAnalyst]) =>
          sampleId && methodTypeId
            ? { sampleId, methodTypeId, poll: selectedAnalyst?.type === 'algorithm' }
            : null
        ),
        distinctUntilChanged(
          (a, b) => a?.sampleId === b?.sampleId && a?.methodTypeId === b?.methodTypeId && a?.poll === b?.poll
        ),
        tap((params) => {
          this.sampleAiProgressStore.loadProgress(of(params));
        })
      )
      .subscribe();
  }

  public readonly reviewing$ = this.selectedAnalyst$.pipe(
    map((selectedAnalyst) => selectedAnalyst?.id !== Parse.User.current()?.id)
  );
  // #endregion

  // #region Protocol selectors
  public readonly pipelines$ = this.protocolStore.pipelines$;
  public readonly steps$ = this.protocolStore.steps$;
  public readonly allOptions$ = this.protocolStore.allOptions$; // Unfiltered options for ROI overlays
  public readonly scopeOptions$ = this.protocolStore.options$; // Scope-filtered options for analysis panels
  public readonly allCategories$ = this.protocolStore.categories$;
  public readonly segmentation$ = this.protocolStore.hasSegmentationTasks$;
  public readonly taggingProtocol$ = this.protocolStore.hasTaggingTasks$;
  public readonly loadingProtocol$ = this.protocolStore.loading$;
  public readonly acquisitionFields$ = this.protocolStore.acquisitionFields$;
  public readonly analysisContext$ = this.protocolStore.scope$;
  public readonly methodTypeId$ = this.protocolStore.methodTypeId$;
  // #endregion

  // #region Navigation selectors
  public readonly caseId$ = this.globalStore.select(
    getRouterSelectors().selectRouteParam('caseid')
  ) as Observable<string>;
  public readonly sampleId$ = this.globalStore.select(
    getRouterSelectors().selectRouteParam('sampleid')
  ) as Observable<string>;
  public readonly historyId$ = this.globalStore.select(
    getRouterSelectors().selectQueryParam('historyId')
  ) as Observable<string>;
  public readonly assetParam$ = this.globalStore.select(
    getRouterSelectors().selectQueryParam('asset')
  ) as Observable<string>;
  public readonly counterFilter$ = this.globalStore.select(
    getRouterSelectors().selectQueryParam('counterFilter')
  ) as Observable<string>;
  public readonly previousCase$ = this.navigationStore.previousCase$;
  public readonly nextCase$ = this.navigationStore.nextCase$;
  public readonly previousSample$ = this.navigationStore.previousSample$;
  public readonly nextSample$ = this.navigationStore.nextSample$;
  public readonly currentCase$ = this.navigationStore.currentCase$;
  public readonly currentSample$ = this.navigationStore.currentSample$;
  public readonly currentWorkspace$ = this.navigationStore.currentWorkspace$;

  // Breadcrumb navigation: selectors live in `NavigationStore`, fetching in the
  // component-provided `BreadcrumbNavigationEffects`, shared with the image screen.
  // The dropdowns cascade, so the user drills workspace → case → sample and commits at
  // the sample — the deepest navigation level here.
  public readonly currentCaseDisplay$ = this.navigationStore.currentCaseDisplay$;
  public readonly loadCasesForWorkspace = (workspaceId: string) =>
    this.breadcrumbNavigationEffects.loadCasesForWorkspace(of(workspaceId));
  public readonly loadSamplesForCase = (caseId: string) =>
    this.breadcrumbNavigationEffects.loadSamplesForCase(of(caseId));

  // Video is a "visualize only" feature: there are no analyses/analysts on video
  // samples, so the breadcrumb exposes only the workspace → case → sample cascade
  // (this store selector already omits any analyst level).
  public readonly breadcrumbLevels$ = this.navigationStore.breadcrumbLevels$;

  /** Analyst-level dropdown opened: load this sample's analysts and their states. */
  public readonly loadAnalysts = (sampleId: string) => this.analystStore.loadAnalysts(sampleId);
  public readonly loadAnalysisStates = (sampleId: string, caseId: string) =>
    this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });

  /**
   * Navigate to a sample choosing the viewer by the sample's real asset type.
   * Video is "visualize only" (no analysts), so no `historyId` is carried. The
   * sample's `isVideo` flag comes from the breadcrumb cascade (`samplesByCaseId`);
   * fall back to the video viewer when the sample/type can't be resolved.
   */
  public readonly navigateToSample = (caseId: string, sampleId: string) => {
    if (!caseId || !sampleId) return;
    this.navigationStore.samplesByCaseId$.pipe(take(1)).subscribe((samplesByCaseId) => {
      const sample = samplesByCaseId?.[caseId]?.find((item) => item.id === sampleId);
      const viewer = sample?.isVideo === false ? 'images' : 'videos';
      this.router.navigate([`analyze/cases/${caseId}/${viewer}/${sampleId}`], {
        queryParams: {},
        replaceUrl: true,
      });
    });
  };
  // #endregion

  // #region Video ROI selectors
  public readonly selectedVideoRois$ = this.videoRoiStore.selectedVideoRois$;
  public readonly selectedVideoRoisEntities$ = this.videoRoiStore.selectedVideoRoisEntities$;
  // #endregion

  // #region Asset store selectors
  public readonly loading$ = this.assetStore.loading$;
  public readonly items$ = this.assetStore.items$;
  public readonly selectedAsset$ = this.assetStore.selectedAsset$;
  public readonly selectedIndex$ = this.assetStore.selectedIndex$;
  public readonly totalAssets$ = this.assetStore.totalAssets$;

  public readonly tileSource$ = this.assetStore.tileSource$;
  public readonly focusPoint$ = this.assetStore.focusPoint$;

  public readonly hspan$ = this.assetStore.hspan$;
  public readonly dataEditable$ = this.assetStore.dataEditable$;
  public readonly updateAssetDataField = this.assetStore.updateAssetDataField;

  public readonly assetIds$ = this.assetStore.assetIds$;
  public readonly assets$ = this.assetStore.assets$;
  public readonly error$ = this.assetStore.error$;
  public readonly metadata$ = this.assetStore.metadata$;
  public readonly selectedAssetId$ = this.assetStore.selectedAssetId$;
  public readonly assetCount$ = this.assetStore.assetCount$;
  // #endregion

  // #region Micro-layout selectors
  // Sourced from the persisted analysis state (see above) so it survives reloads.
  public readonly bookmarkedAsset$ = this.currentAnalystBookmarkedAsset$;
  public readonly acquisitionEdition$ = this.microLayout.acquisitionEdition$;
  public readonly collapsedCategories$ = this.microLayout.collapsedCategories$;
  public readonly multiSelection$ = this.microLayout.multiSelection$;
  public readonly hiddenLabels$ = this.microLayout.hiddenLabels$;
  public readonly pinnedLabels$ = this.microLayout.pinnedLabels$;
  public readonly viewerMode$ = this.microLayout.viewerMode$;
  public readonly protocolPanelCollapsed$ = this.microLayout.protocolPanelCollapsed$;

  /** Collapse/expand the protocol side panel (compact colours+numbers rail). */
  public toggleProtocolPanel(): void {
    this.microLayout.toggleProtocolPanel();
  }
  public readonly excludedFromCounter$ = this.roiStatsStore.counterExcludedLabels$;

  public readonly statsMode$ = this.select(this.roiStatsStore.counterExclusionMode$, (mode) =>
    mode === 'selective' ? StatsMode.SELECTIVE : StatsMode.COMPLETE
  );

  // Derived selectors for backward compatibility with counterMode and percentageBaseMode
  public readonly counterMode$ = this.select(this.statsMode$, () => CounterMode.percentage);

  public readonly drawingMode$ = this.microLayout.drawingMode$;
  public readonly tabActiveContext$ = this.microLayout.tabContext$;
  public readonly selectedPreset$ = this.microLayout.selectedPreset$;
  public readonly cursor$ = this.microLayout.cursorSelector$;
  public readonly showPinned$ = this.microLayout.showPinnedOnly$;
  public readonly availableDrawingModes$ = this.microLayout.availableDrawingModes$;
  public readonly imageFilters$ = this.microLayout.imageFilters$;
  public readonly rulerActive$ = this.microLayout.rulerActive$;
  public readonly rulerIcon$ = this.microLayout.rulerType$;
  public readonly rulerSize$ = this.select(this.microLayout.rulerSize$, this.assetStore.hspan$, (rulerSize, hspan) => {
    if (rulerSize != null) return rulerSize;
    if (hspan != null) return Math.round((hspan * 100) / 5) * 5;
    return null;
  });
  public readonly assetFilters$ = this.microLayout.assetFilters$;
  public readonly viewedAssets$ = this.microLayout.viewedAssets$.pipe(map((assets) => Array.from(assets)));
  public readonly reviewedAssets$ = this.microLayout.reviewedAssets$.pipe(map((assets) => Array.from(assets)));
  public readonly analyzedAssets$ = this.microLayout.analyzedAssets$.pipe(map((assets) => Array.from(assets))); //review when analysis for video rois is implemented
  // #endregion

  // #region Computed selectors
  public readonly getProgress$ = this.select(
    this.viewedAssets$,
    this.totalAssets$,
    (viewedAssets, totalAssets) => Math.floor((viewedAssets.length / (totalAssets ?? 0)) * 100) ?? 0
  );
  public readonly selectedAssetItem$ = this.select(this.items$, this.selectedIndex$, (items, selectedIndex) => {
    if (items.length === 0) return null;
    return items[selectedIndex];
  });

  public readonly videoBlobUrl$ = this.selectedAssetItem$.pipe(
    switchMap((selectedItem) => {
      if (!selectedItem?.url) return of(null);

      return from(
        new Promise<string>((resolve, reject) => {
          const request = new XMLHttpRequest();
          request.responseType = 'blob';
          request.open('get', selectedItem.url, true);

          const sessionToken = Parse.User.current()?.getSessionToken();
          if (sessionToken) {
            request.setRequestHeader('X-Parse-Session-Token', sessionToken);
          }

          request.onreadystatechange = () => {
            if (request.readyState === XMLHttpRequest.DONE) {
              if (request.status === 200) {
                resolve(request.responseURL); // blob URL
              } else {
                reject(`Failed to fetch video: ${request.status}`);
              }
            }
          };
          request.send(null);
        })
      ).pipe(
        catchError((error) => {
          console.error('Error loading video blob:', error);
          return of(null);
        })
      );
    })
  );
  // #endregion

  // #region Computed selectors (continued)
  public readonly selectedLabelsSelector$ = this.select(
    this.scopeOptions$,
    this.protocolStore.pipelines$,
    this.protocolStore.categories$,
    this.microLayout.selectedLabels$,
    (options, pipelines, categories, selectedLabelIds) => {
      if (!options || !categories) return [];

      if (!selectedLabelIds || selectedLabelIds.size === 0) return [];

      const labels = Object.values(options)
        .filter((option) => selectedLabelIds.has(option.id))
        .map((option) => {
          const category = Object.values(categories).find((cat) => cat.options.includes(option.id));
          const pipeline = pipelines.find((pl) => pl.categories.includes(category?.id));
          return {
            ...option,
            category: category?.name,
            stepId: category?.stepId,
            pipelineId: pipeline?.id,
          };
        });

      return labels;
    }
  );

  public readonly selectedLabels$ = this.selectedLabelsSelector$;

  public readonly availableViewerModes$ = this.select(
    this.protocolStore.hasTaggingTasks$,
    this.protocolStore.hasSegmentationTasks$,
    (hasROITasks, hasSegmTasks) => [
      ViewerMode.idle,
      ...(hasROITasks ? [ViewerMode.drawing, ViewerMode.selecting] : []),
      ...(hasSegmTasks ? [ViewerMode.free] : []),
    ]
  );

  public readonly selectedToolIndex$ = this.select(
    this.availableViewerModes$,
    this.viewerMode$,
    (availableModes, activeMode) => (availableModes.includes(activeMode) ? availableModes.indexOf(activeMode) : null)
  );

  public readonly visibleLabels$ = this.select(this.scopeOptions$, this.hiddenLabels$, (labels, hiddenLabels) => {
    if (!labels) return [];
    return Object.keys(labels).filter((labelId) => !hiddenLabels.has(labelId));
  });

  public readonly visibleOptions$ = this.select(
    this.scopeOptions$,
    this.showPinned$,
    this.pinnedLabels$,
    (labels, showOnlyPinned, pinnedLabels) => {
      if (!labels) return {};

      if (!showOnlyPinned) return labels;

      return Object.fromEntries(Object.entries(labels).filter(([key]) => pinnedLabels.has(key)));
    }
  );

  public readonly visibleRois$ = this.select(
    this.videoRoiStore.videoRois$,
    this.hiddenLabels$,
    this.allOptions$,
    (videoRois, hiddenLabels, allOptions) => {
      if (!videoRois || Object.keys(videoRois).length === 0) {
        return [];
      }

      return Object.values(videoRois).filter((videoRoi) => {
        return videoRoi.keyframes.some((keyframe) => {
          const keyframeLabels = Array.from(keyframe.labels.entries());

          return keyframeLabels.some(([labelId, score]) => {
            if (hiddenLabels.has(labelId)) {
              return false;
            }

            const labelOption = allOptions[labelId];
            const threshold = labelOption?.threshold || 0;
            return score >= threshold;
          });
        });
      });
    }
  );

  //TODO: pending to handle stats in videorois store
  public readonly labelCounts$ = new Map();

  /** Video ROI counts by label for current asset (raw, no exclusion) – for display when label is excluded */
  public readonly assetVideoRoiCountsByLabel$ = this.select(
    this.selectedAsset$,
    this.videoRoiStore.assetIndex$,
    this.videoRoiStore.videoRois$,
    (selectedAsset, assetIndex, videoRois) => {
      if (!selectedAsset?.id || !videoRois) return new Map<string, number>();

      const assetVideoRoiIds = assetIndex.get(selectedAsset.id) || new Set<string>();
      const videoRoiCountsByLabel = new Map<string, number>();

      const getLabelsForVideoRoi = (videoRoi: IVideoROI): string[] => {
        const allLabels = new Set<string>();
        videoRoi.keyframes?.forEach((keyframe) => {
          keyframe.labels?.forEach?.((_: unknown, labelId: string) => allLabels.add(labelId));
        });
        return Array.from(allLabels);
      };

      assetVideoRoiIds.forEach((videoRoiId) => {
        const videoRoi = videoRois[videoRoiId];
        if (!videoRoi) return;

        const labels = getLabelsForVideoRoi(videoRoi);
        labels.forEach((labelId) => {
          videoRoiCountsByLabel.set(labelId, (videoRoiCountsByLabel.get(labelId) ?? 0) + 1);
        });
      });

      return videoRoiCountsByLabel;
    }
  );

  public readonly activeAssetVideoRoiCountsByLabel$ = this.select(
    this.selectedAsset$,
    this.videoRoiStore.assetIndex$,
    this.videoRoiStore.videoRois$,
    this.roiStatsStore.counterExcludedLabels$,
    this.roiStatsStore.counterExclusionMode$,
    (selectedAsset, assetIndex, videoRois, counterExcludedLabels, counterExclusionMode) => {
      if (!selectedAsset?.id || !videoRois) return new Map<string, number>();

      const activeAssetVideoRoiIds = assetIndex.get(selectedAsset.id) || new Set<string>();
      const videoRoiCountsByLabel = new Map<string, number>();

      const isMultiActiveComplete = (labels: string[]) => !labels.some((l) => counterExcludedLabels.has(l));
      const isMultiActiveSelective = (labels: string[]) => !labels.every((l) => counterExcludedLabels.has(l));

      const getLabelsForVideoRoi = (videoRoi: IVideoROI): string[] => {
        const allLabels = new Set<string>();
        videoRoi.keyframes?.forEach((keyframe) => {
          keyframe.labels?.forEach?.((_: unknown, labelId: string) => allLabels.add(labelId));
        });
        return Array.from(allLabels);
      };

      activeAssetVideoRoiIds.forEach((videoRoiId) => {
        const videoRoi = videoRois[videoRoiId];
        if (!videoRoi) return;

        const labels = getLabelsForVideoRoi(videoRoi);
        if (labels.length === 0) return;

        if (labels.length === 1) {
          if (!counterExcludedLabels.has(labels[0])) {
            videoRoiCountsByLabel.set(labels[0], (videoRoiCountsByLabel.get(labels[0]) ?? 0) + 1);
          }
        } else {
          const isActive =
            counterExclusionMode === 'complete' ? isMultiActiveComplete(labels) : isMultiActiveSelective(labels);
          if (isActive) {
            labels.forEach((labelId) => {
              videoRoiCountsByLabel.set(labelId, (videoRoiCountsByLabel.get(labelId) ?? 0) + 1);
            });
          }
        }
      });

      return videoRoiCountsByLabel;
    }
  );

  // TODO: implement - calculate total count from Video ROI store
  public readonly totalCount$ = of(0);

  // TODO: implement - calculate total stats from Video ROI store and labels
  public readonly totalStats$ = of([]);

  public readonly activeAssetFindings$ = this.select(
    this.selectedAsset$,
    this.selectedAnalyst$,
    this.findingStore.findingsWithMetadata$,
    (selectedAsset, selectedAnalyst, findings) => {
      if (!selectedAsset || !selectedAnalyst || !findings) {
        return [];
      }

      const assetId = selectedAsset.id;
      const analystId = selectedAnalyst.id;
      const analystEntity = selectedAnalyst.type;

      return findings.filter(
        (finding: any) =>
          finding.assetId === assetId && finding.creatorId === analystId && finding.creatorEntity === analystEntity
      );
    }
  );

  public readonly activeSampleFindings$ = this.select(
    this.selectedAnalyst$,
    this.findingStore.sampleFindingsWithMetadata$,
    (selectedAnalyst, sampleFindings) => {
      if (!selectedAnalyst || !sampleFindings) {
        return [];
      }

      const analystId = selectedAnalyst.id;
      const analystEntity = selectedAnalyst.type;

      return sampleFindings.filter(
        (finding: any) => finding.creatorId === analystId && finding.creatorEntity === analystEntity
      );
    }
  );

  public readonly activeFindings$ = this.select(
    this.protocolStore.scope$,
    this.activeAssetFindings$,
    this.activeSampleFindings$,
    (scope, assetFindings, sampleFindings) => {
      return scope === ProtocolScope.Asset ? assetFindings : sampleFindings;
    }
  );

  public readonly videoRoiOverlays$ = this.select(
    this.visibleRois$,
    this.selectedAsset$,
    this.allOptions$,
    this.selectedVideoRois$,
    (rois, selectedAsset, allOptions, selectedRois) => {
      if (!selectedAsset || !rois.length) return [];

      const assetWidth = selectedAsset.data?.width || 1;
      const assetHeight = selectedAsset.data?.height || 1;

      return rois
        .map((videoRoi) => {
          const firstKeyframe = videoRoi.keyframes[0];
          if (!firstKeyframe) return null;

          const x = firstKeyframe.coordinates.x0 * assetWidth;
          const y = firstKeyframe.coordinates.y0 * assetHeight;
          const w = (firstKeyframe.coordinates.x1 - firstKeyframe.coordinates.x0) * assetWidth;
          const h = (firstKeyframe.coordinates.y1 - firstKeyframe.coordinates.y0) * assetHeight;

          // Get labels from the first keyframe
          const firstLabelId = Array.from(firstKeyframe.labels.keys())[0];
          const firstLabel = allOptions[firstLabelId];
          const color = firstLabel?.color;

          const selected = selectedRois?.has(videoRoi.id) || false;

          // Collect all unique labels from all keyframes
          const allLabelIds = new Set<string>();
          videoRoi.keyframes.forEach((keyframe) => {
            keyframe.labels.forEach((_, labelId) => allLabelIds.add(labelId));
          });

          const labelNames = Array.from(allLabelIds)
            .map((labelId) => allOptions[labelId]?.name || '')
            .filter((name) => name)
            .join(', ');

          const tooltip = labelNames || '(no tags)';

          // Get time range from keyframes
          const timestamps = videoRoi.keyframes.map((kf) => kf.timestamp).sort((a, b) => a - b);
          const t1 = timestamps[0];
          const t2 = timestamps[timestamps.length - 1];

          return {
            id: videoRoi.id,
            x,
            y,
            w,
            h,
            color,
            t1,
            t2,
            selected,
            tooltip,
          } as VideoROIOverlay;
        })
        .filter(Boolean);
    }
  );

  public readonly ready$ = this.select(
    this.protocolStore.loading$,
    this.analystsLoading$,
    this.analysisStatesLoading$,
    (protocolLoading, analystsLoading, analysisStatesLoading) =>
      !protocolLoading && !analystsLoading && !analysisStatesLoading
  );
  public readonly syncing$ = this.select(
    this.findingStore.syncingChanges$,
    this.videoRoiStore.syncingChanges$,
    (findingSyncing, roiSyncing) => findingSyncing || roiSyncing
  );

  public readonly unsynced$ = this.select(
    this.findingStore.unsynced$,
    this.videoRoiStore.unsynced$,
    (findingUnsynced, videoRoiUnsynced) => findingUnsynced || videoRoiUnsynced
  );

  public readonly synced$ = this.select(
    this.findingStore.synced$,
    this.videoRoiStore.synced$,
    (findingSynced, videoRoiSynced) => findingSynced && videoRoiSynced
  );
  // #endregion

  // Navigation
  private readonly navigateToCaseDetailInput$ = this.navigateToCaseDetailTrigger$.pipe(
    concatLatestFrom(() => this.caseId$),
    switchMap(([, caseId]) => from(this.router.navigateByUrl(`/analyze/cases/${caseId}`)))
  );

  // #region asset effects
  public readonly selectAsset = this.effect((assetId$: Observable<string | null>) =>
    assetId$.pipe(
      map((assetId) => {
        this.assetStore.selectAsset(assetId);
        this.microLayout.addViewedAsset(assetId);
      })
    )
  );
  // #endregion

  // #region Effects

  // Analyst Management
  public readonly setCurrentAnalyst = this.effect((params$: Observable<{ sampleId: string; caseId: string }>) =>
    params$.pipe(
      switchMap(({ sampleId, caseId }) => {
        const currentUserId = Parse.User.current()?.id;
        if (!currentUserId) return EMPTY;

        return from(this.analysisStateApi.getUserState(currentUserId, null, sampleId)).pipe(
          switchMap((analysisState) =>
            analysisState
              ? of(analysisState)
              : from(
                  this.analysisStateApi.saveAnalysisState(
                    {
                      id: undefined,
                      analystId: currentUserId,
                      analystEntity: 'user' as AnalyzerEntity,
                      state: IState.READ,
                      reference: false,
                      bookmarkAssetId: undefined,
                      favouriteAssetIds: [],
                    },
                    sampleId,
                    null
                  )
                )
          ),
          tap((analysisState) => {
            this.analysisStateStore.updateAnalysisState(analysisState);
            this.analystStore.addAnalyst({
              id: analysisState.analystId,
              historyId: analysisState.id,
              type: analysisState.analystEntity as 'user' | 'algorithm',
              name: analysisState.name || Parse.User.current()?.get('username') || '',
            });
            this.loadAnalysts$.next({ sampleId, caseId });
          }),
          switchMap(() => EMPTY),
          catchError(() => EMPTY)
        );
      })
    )
  );

  private readonly loadAnalystsEffect = this.effect((params$: Observable<{ sampleId: string; caseId: string }>) =>
    params$.pipe(
      switchMap(({ sampleId, caseId }) => {
        this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });
        this.analystStore.loadAnalysts(sampleId);
        return EMPTY;
      })
    )
  );

  // Analysis State Management
  public readonly updateSampleState = this.effect((state$: Observable<IState>) => {
    return state$.pipe(
      concatLatestFrom(() => [this.sampleId$]),
      switchMap(([state, sampleId]) => {
        this.analysisStateStore.updateSampleState({ sampleId, state });
        return EMPTY;
      })
    );
  });

  readonly updateCaseState = this.effect((state$: Observable<IState>) => {
    return state$.pipe(
      concatLatestFrom(() => this.currentCase$),
      switchMap(([state, currentCase]) => {
        this.analysisStateStore.updateCaseState({
          state,
          currentCaseId: currentCase.id,
        });

        return of(null);
      })
    );
  });

  readonly setReference = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      concatLatestFrom(() => [this.currentSample$]),
      switchMap(([, sample]) => {
        this.analysisStateStore.setReference(sample.id);
        return EMPTY;
      })
    );
  });

  // Progress Management
  readonly loadProgress = this.effect((sampleId$: Observable<string>) => {
    return sampleId$.pipe(
      map((sampleId) => {
        const storageData = JSON.parse(localStorage.getItem(`sample_${sampleId}`));
        const scannedAssets = storageData?.scannedAssets || [];

        this.microLayout.setViewedAssets(scannedAssets);
      })
    );
  });

  readonly saveProgress = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      concatLatestFrom(() => [this.currentSample$, this.viewedAssets$]),
      map(([, currentSample, viewedAssets]) => {
        localStorage.setItem(`sample_${currentSample.id}`, JSON.stringify({ scannedAssets: viewedAssets }));
      })
    );
  });

  // Asset Management
  public readonly fetchReferenceStrip = this.effect((params$: Observable<{ sampleId: string; analyst: Analyst }>) =>
    params$.pipe(
      concatLatestFrom(() => [this.assetStore.assetIds$, this.findingStore.findings$]),
      switchMap(([{ sampleId, analyst }, assetIds, findings]) => {
        if (!assetIds.length) return EMPTY;

        this.microLayout.fetchReferenceStripInfo({
          sampleId,
          analyst,
          assetIds,
          localFindings: Object.values(findings) as FindingParameters[],
        });

        return EMPTY;
      })
    )
  );

  // Findings
  public readonly fetchAssetFindings = this.effect(
    (action$: Observable<{ assetId: string; historyId: string; sampleId: string }>) =>
      action$.pipe(
        concatLatestFrom(() => [
          this.steps$,
          this.analysisStates$,
          this.analystStore.analysts$,
        ]),
        switchMap(([{ sampleId, assetId, historyId }, steps, analysisStates, analysts]) => {
          let analystId, analystEntity;

          if (!historyId) {
            analystId = Parse.User.current()?.id;
            analystEntity = 'user';
          } else {
            const analysisState = Array.from(analysisStates.values()).find((state) => state.id === historyId);
            analystId = analysisState.analystId;
            analystEntity = analysts.find((analyst) => analyst.id === analysisState.analystId)?.type;
          }

          const pipelineStepIds = steps.map((s) => s.id);

          // TODO: Implement ROI loading for video

          this.findingStore.fetchAssetFindings({ assetId, sampleId, analystId, analystEntity, pipelineStepIds });

          return EMPTY;
        })
      )
  );

  public readonly fetchSampleFindings = this.effect((action$: Observable<{ analyst: Analyst; sampleId: string }>) =>
    action$.pipe(
      concatLatestFrom(() => [this.steps$, this.findingStore.sampleFindings$]),
      tap(([{ sampleId, analyst }, steps, findings]) => {
        const hasCachedFindings = findings.some(
          (finding: FindingItem) => finding.creatorId === analyst.id && finding.creatorEntity === analyst.type
        );

        if (hasCachedFindings) return;

        this.findingStore.fetchSampleFindings({
          sampleId: sampleId,
          pipelineStepIds: steps.map((s) => s.id),
          analystEntity: analyst.type,
          analystId: analyst.id,
        });
      })
    )
  );

  public readonly updateFinding = this.effect(
    (action$: Observable<{ id: string; taskType: 'text' | 'classification'; value: string | string[] }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.canEdit$]),
        switchMap(([{ id, taskType, value }, canEdit]) => {
          if (!canEdit) return EMPTY;

          this.findingStore.updateFinding({
            findingId: id,
            data: value,
            type: taskType,
          });

          return EMPTY;
        }),
        catchError((error) => {
          console.error('Error updating finding:', error);
          return EMPTY;
        })
      )
  );

  public readonly createFinding = this.effect(
    (
      action$: Observable<{
        taskType: StepTask;
        value: string | string[];
        pipelineId: string;
        taskId: string;
      }>
    ) =>
      action$.pipe(
        concatLatestFrom(() => [
          this.selectedAsset$,
          this.selectedAnalyst$,
          this.activeFindings$,
          this.protocolStore.scope$,
        ]),
        switchMap(
          ([{ taskType, value, pipelineId, taskId }, selectedAsset, selectedAnalyst, activeFindings, scope]) => {
            const assetId = scope === ProtocolScope.Asset ? selectedAsset?.id : undefined;
            const analystId = selectedAnalyst.id;
            const analystEntity = selectedAnalyst.type;
            if (!analystId) return EMPTY;

            const pipelineFindings = activeFindings.filter((finding: any) => finding.pipelineId === pipelineId);

            this.findingStore.createFinding({
              assetId,
              creatorId: analystId,
              creatorEntity: analystEntity,
              pipelineId,
              taskId,
              taskType,
              value,
              existingFindings: pipelineFindings as any,
            });

            return EMPTY;
          }
        )
      )
  );

  // Video ROI
  public readonly addRois = this.effect((action$: Observable<{ overlay: any }>) =>
    action$.pipe(
      concatLatestFrom(() => [
        this.selectedAsset$,
        this.currentSample$,
        this.selectedLabels$,
        this.findingStore.findings$,
        this.allCategories$,
      ]),
      switchMap(([{ overlay }, selectedAsset, currentSample, selectedLabels, findings, categories]) => {
        const assetId = selectedAsset?.id;
        const sampleId = currentSample?.id;
        const creatorId = Parse.User.current()?.id;

        if (!assetId || !sampleId || !creatorId) {
          return EMPTY;
        }

        // Normalize coordinates based on asset dimensions
        const assetWidth = selectedAsset?.data?.width || 1;
        const assetHeight = selectedAsset?.data?.height || 1;

        const coordinates = {
          x0: (overlay.x || 0) / assetWidth,
          y0: (overlay.y || 0) / assetHeight,
          x1: ((overlay.x || 0) + (overlay.w || 0)) / assetWidth,
          y1: ((overlay.y || 0) + (overlay.h || 0)) / assetHeight,
        };

        // TODO: Implement ROI creation logic for video
        console.log('Creating video ROI with normalized coordinates:', coordinates);

        return of(null);
      })
    )
  );

  readonly onSelectLabel = this.effect((labelId$: Observable<string>) =>
    labelId$.pipe(
      concatLatestFrom(() => [
        this.selectedVideoRois$,
        this.viewerMode$,
        this.allCategories$,
        this.multiSelection$,
        this.microLayout.selectedLabels$,
      ]),
      tap(([labelId, selectedRois, viewerMode, categories, multiselectEnabled, selectedLabelIds]) => {
        const activeTask = Object.values(categories || {}).find((c: Category) => c.options?.includes(labelId)) as
          | Category
          | undefined;

        if (selectedRois.size > 0 || !activeTask) return;

        if (multiselectEnabled && selectedLabelIds?.size) {
          const otherInCategory = activeTask.options.filter(
            (optId) => optId !== labelId && selectedLabelIds.has(optId)
          );
          if (otherInCategory.length > 0) {
            this.microLayout.removeLabels(otherInCategory);
          }
        }
        this.microLayout.selectLabel({ uuid: labelId, replace: !multiselectEnabled });

        //call addLabels

        const taskType = activeTask.stepTask;
        const isTaggingTask = taskType === StepTask.ROITRACKING;

        if (isTaggingTask && viewerMode !== ViewerMode.drawing) {
          this.microLayout.setViewerMode(ViewerMode.drawing);
          this.microLayout.setDrawingMode(DrawingMode.boundingBox);
        }
      })
    )
  );

  // Sync/Discard
  public readonly syncChangesEffect = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => [this.sampleId$, this.selectedAnalyst$, this.aiAnalysisStatus$]),
      switchMap(([, sampleId, selectedAnalyst, status]) => {
        if (!sampleId || !selectedAnalyst?.id) {
          return EMPTY;
        }

        if (algorithmAiAnalysisLocked(status, selectedAnalyst)) {
          this.saveBlockedByAiAnalysisSubject.next();
          return EMPTY;
        }

        const currentAnalystId = Parse.User.current()?.id;
        const sourceAnalystId = selectedAnalyst.id;

        if (!currentAnalystId) {
          return EMPTY;
        }

        sourceAnalystId === currentAnalystId
          ? this.findingStore.syncChanges({ sampleId, currentAnalystId })
          : this.findingStore.reviewAnalysis({ sampleId, currentAnalystId, sourceAnalystId });

        return EMPTY;
      })
    )
  );

  public readonly discardChangesEffect = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => [
        this.sampleId$,
        this.selectedAnalyst$,
        this.videoRoiStore.changedVideoRois$,
        this.findingStore.changedFindings$,
      ]),
      switchMap(([, sampleId, selectedAnalyst, changedVideoRois, changedFindings]) => {
        if (!sampleId || !selectedAnalyst?.id) {
          return EMPTY;
        }

        const currentAnalystId = Parse.User.current()?.id;

        if (!currentAnalystId) {
          return EMPTY;
        }

        //TODO: Implement revert logic

        const changedFindingIds = Array.from(changedFindings.keys());
        if (changedFindingIds.length > 0) {
          this.findingStore.revertChanges(changedFindingIds);
        }

        return EMPTY;
      })
    )
  );

  // Public method to trigger sync
  public syncChanges(): void {
    this.syncChangesTrigger$.next();
  }

  // Public method to trigger discard changes
  public discardChanges(): void {
    this.discardChangesTrigger$.next();
  }
  // #endregion

  // #region Counter methods
  // Counter

  readonly onCounterLabel = (labelId: string) => this.roiStatsEffects.toggleCounterExcluded(labelId);

  // #endregion

  // #region Protocol methods
  public setProtocolContext(context: ProtocolScope) {
    this.protocolStore.setScope(context);
  }

  public fetchProtocol(sampleId$: Observable<string>) {
    this.protocolStore.selectSample(sampleId$);
  }
  // #endregion

  // #region Navigation methods
  public fetchNavigationReferences(sampleId: string) {
    this.breadcrumbNavigationEffects.fetchNavigationReferences(sampleId);
  }

  public navigateToCaseDetail() {
    this.navigateToCaseDetailTrigger$.next();
  }
  // #endregion

  // #region Query params methods

  public updateQueryParams(queryParams: any) {
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
  // #endregion
  // #region Micro-layout methods
  public setViewerMode(mode: ViewerMode) {
    this.microLayout.setViewerMode(mode);
  }

  /** Enable/disable editing while reviewing another analyst's analysis (mirrors the image screen). */
  public toggleEditing(enabled: boolean) {
    this.microLayout.toggleEditing(enabled);
  }

  public setDrawingMode(mode: DrawingMode) {
    this.microLayout.setDrawingMode(mode);
  }

  public setTabContext(ctx: ETabContext) {
    this.microLayout.setTabContext(ctx);
  }

  public setSelectedPreset(preset: number | null) {
    this.microLayout.setSelectedPreset(preset);
  }

  public resetRuler() {
    this.microLayout.resetRuler();
  }

  public changeRulerIcon(type: RulerType) {
    this.microLayout.setRulerType(type);
  }

  public changeRulerSize(size: number) {
    this.microLayout.setRulerSize(size);
  }

  public updateImageFilter(filter: ImageFilter) {
    this.microLayout.updateImageFilter(filter);
  }

  public updateImageFilters(data) {
    this.microLayout.updateImageFilters(data);
  }

  public updateImageFiltersBulk(filters: ImageFilter[]) {
    this.microLayout.updateImageFiltersBulk(filters);
  }

  public resetImageFilters() {
    this.microLayout.resetImageFilters();
  }

  public selectLabel(labelUuid: string, replace = false) {
    this.microLayout.selectLabel({ uuid: labelUuid, replace });
  }

  public clearSelectedLabels() {
    this.microLayout.clearSelectedLabels();
  }

  public selectMultipleLabels(uuids: string[]) {
    this.microLayout.selectMultipleLabels(uuids);
  }

  public collapseCategory(label: string) {
    this.microLayout.toggleCollapsedCategory(label);
  }

  public onPinnedLabel(label: string) {
    this.microLayout.togglePinnedLabel(label);
  }

  public onVisibleLabel(label: string) {
    this.microLayout.toggleHiddenLabel(label);
  }

  public toggleMultiSelection() {
    this.microLayout.toggleMultiSelection();
  }

  public togglePinnedOptions() {
    this.microLayout.toggleShowPinnedOnly();
  }

  public toogleAcquisitionEdition(value: boolean) {
    this.microLayout.setAcquisitionEdition(value);
  }

  public setStatsMode(mode: StatsMode) {
    this.roiStatsEffects.toggleCounterExclusionMode(mode === StatsMode.SELECTIVE ? 'selective' : 'complete');
  }

  public selectRois(roiIds: string[]) {
    this.microLayout.selectRois(roiIds);
  }

  public deselectRois() {
    this.microLayout.deselectRois();
  }

  public addViewedAsset(assetId: string) {
    this.microLayout.addViewedAsset(assetId);
  }

  public updateAssetFilters(filter: 'analyzed' | 'favAcquisition' | 'favAnalysis') {
    this.microLayout.updateAssetFilters(filter);
  }
  // #endregion

  // #region Video ROI methods
  public clearSelectedVideoRois() {
    this.videoRoiStore.clearSelectedVideoRois();
  }

  public selectVideoRois(videoRoiIds: string[]) {
    this.videoRoiStore.selectVideoRois(videoRoiIds);
  }

  public updateVideoRoiTime(videoRoiId: string, changes: { t1?: number; t2?: number }) {
    this.videoRoiStore.updateVideoRoiTime({ videoRoiId, changes });
  }

  public removeAllLabels(roiIds: string[]) {
    this.videoRoiStore.removeAllLabels(roiIds);
  }
  // #endregion

  // #region Asset methods

  public fetchAssetInfo(assetId: string) {
    this.assetStore.fetchAssetInfo(assetId);
  }

  public loadSampleAssets(sampleId: string) {
    this.assetStore.loadSampleAssets(of(sampleId));
  }
  // #endregion
}
